import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, type LanguageView, type LanguagesView } from "../api.js";
import { BILLING_CHANGED } from "../components/BillingChip.js";
import { anyInProgress, finishedSince } from "../languages/languages.js";
import { keepsSelection } from "./languageView.js";

const POLL_MS = 5_000;

/**
 * The editor's languages: what this video has been made in, whether the
 * Languages panel is open (`?languages=open`) and which language is shown
 * (`?lang=de`). Both live in the URL, so a reload keeps them. A shown
 * language stays shown while it is voiced or revoiced; only a code that is
 * gone or failed falls back to the English and leaves the URL.
 */
export function useLanguageView(projectId: string) {
  const [view, setView] = useState<LanguagesView | null>(null);
  const [params, setParams] = useSearchParams();
  const panelOpen = params.get("languages") === "open";
  const requested = params.get("lang");

  const before = useRef<LanguagesView["languages"] | null>(null);
  const reload = useCallback(() => {
    void api
      .languages(projectId)
      .then((next) => {
        // A language was charged, or its hold let go: the balance moved.
        if (finishedSince(before.current, next.languages)) window.dispatchEvent(new Event(BILLING_CHANGED));
        before.current = next.languages;
        setView(next);
      })
      .catch(() => undefined);
  }, [projectId]);
  useEffect(reload, [reload]);

  // The panel polls for itself while it is open; closed, this keeps the switch current.
  const polling = anyInProgress(view) && !panelOpen;
  const latest = useRef(reload);
  latest.current = reload;
  useEffect(() => {
    if (!polling) return;
    const timer = window.setInterval(() => latest.current(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [polling]);

  const language = useMemo<LanguageView | null>(
    () =>
      view && requested && keepsSelection(view, requested)
        ? (view.languages.find((one) => one.code === requested) ?? null)
        : null,
    [view, requested],
  );

  const setParam = useCallback(
    (name: string, value: string | null) =>
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value === null) next.delete(name);
          else next.set(name, value);
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );

  useEffect(() => {
    if (view && requested && !language) setParam("lang", null);
  }, [view, requested, language, setParam]);

  const setLanguageCode = useCallback((code: string | null) => setParam("lang", code), [setParam]);
  const setPanelOpen = useCallback((open: boolean) => setParam("languages", open ? "open" : null), [setParam]);

  return { view, reload, panelOpen, setPanelOpen, language, setLanguageCode };
}
